import { useLayoutEffect, useMemo, useRef } from 'react';

import { createScrollController } from './createScrollController';

interface UseScrollerParams {
    itemSelector?: string;
    initialIndex?: number;
    onResize?: (width: number, height: number) => void;
}

export const useScroller = <T extends HTMLElement>({
    itemSelector,
    initialIndex,
    onResize,
}: UseScrollerParams = {}) => {
    const scrollerRef = useRef<T>(null);

    const scroller = useMemo(
        () => createScrollController({ itemSelector, onResize }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [itemSelector]
    );

    useLayoutEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;

        scroller.setContainer(el);

        return scroller.destroy;
    }, [scroller]);

    useLayoutEffect(() => {
        if (initialIndex !== undefined) {
            scroller.scrollToIndex(initialIndex, { behavior: 'instant' });
        }
    }, [scroller, initialIndex]);

    return { scrollerRef, scroller };
};
